/**
 * 画布左上角文件工具条：可编辑文件名
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconEdit } from '@tabler/icons-react';
import { Button, Input, Toolbar, cn } from '@heroui/react';
import { useWorkspaceChrome } from '@/components/canvas/workspace/stage/WorkspaceChromeContext';

export interface FileSettingsToolbarProps {
  currentFileName: string;
  onRenameFile?: (newName: string) => void | Promise<unknown>;
  className?: string;
  style?: React.CSSProperties;
}

export function FileSettingsToolbar({
  currentFileName,
  onRenameFile,
  className,
  style,
}: FileSettingsToolbarProps) {
  const chrome = useWorkspaceChrome();
  const isReadonly = chrome?.isReadonly ?? false;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(currentFileName);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayName = currentFileName.trim() || '未命名';

  useEffect(() => {
    if (!isEditing) setEditValue(currentFileName);
  }, [currentFileName, isEditing]);

  useEffect(() => {
    if (isReadonly && isEditing) {
      setIsEditing(false);
      setEditValue(currentFileName);
    }
  }, [isReadonly, isEditing, currentFileName]);

  useEffect(() => {
    if (!isEditing) return;
    const t = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, [isEditing]);

  const commitRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && onRenameFile && trimmed !== currentFileName.trim()) {
      onRenameFile(trimmed);
    }
    setIsEditing(false);
  }, [editValue, onRenameFile, currentFileName]);

  const cancelEdit = useCallback(() => {
    setEditValue(currentFileName);
    setIsEditing(false);
  }, [currentFileName]);

  const startEdit = useCallback(() => {
    if (!onRenameFile || isReadonly) return;
    setEditValue(currentFileName);
    setIsEditing(true);
  }, [onRenameFile, isReadonly, currentFileName]);

  return (
    <div
      className={cn('absolute left-2 top-2 z-[50]', className)}
      style={style}
    >
      <Toolbar
        aria-label="文件名称"
        className="max-w-[min(48vw,280px)] bg-default px-3 py-1 shadow-none rounded-4xl"
      >
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            className="min-w-[8rem] max-w-[min(44vw,240px)] rounded-full"
            aria-label="编辑文件名"
            disabled={isReadonly}
          />
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className={cn(
                'min-w-0 truncate text-sm text-default-foreground',
                onRenameFile && !isReadonly && 'cursor-text hover:opacity-80',
              )}
              onClick={startEdit}
              disabled={!onRenameFile || isReadonly}
              aria-label={`文件名：${displayName}`}
            >
              {displayName}
            </button>
            {onRenameFile && !isReadonly ? (
              <Button
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="编辑文件名"
                onPress={startEdit}
              >
                <IconEdit className="size-4" stroke={1.25} />
              </Button>
            ) : null}
          </div>
        )}
      </Toolbar>
    </div>
  );
}
